import { type AdminClientInvoice } from '@carsfinders/contracts';
import { useId } from 'react';

import { t } from '../../../../lib/i18n';
import { INVOICE_STATUS_TEXT, invoiceStatusKey } from '../../../finance/utils';
import { isPayable } from './allocation';
import { formatChf, formatDate } from './finance-format';
import { cardClass, headingClass } from './finance-ui';

const GRID =
  'grid min-w-[640px] grid-cols-[120px_minmax(0,1.6fr)_96px_110px_120px_96px] items-center gap-3';

/**
 * «Рахунки» — the client's own invoices from the read model: no · car · due ·
 * outstanding · status, and «Оплатити» on an invoice that still owes, which
 * opens «Додати платіж» with it preselected. The button is not rendered while
 * `PAYMENTS_ENABLED` is off: the server would refuse the invoice part.
 */
export function ClientInvoicesTable({
  invoices,
  paymentsEnabled,
  onPay,
}: {
  invoices: AdminClientInvoice[];
  paymentsEnabled: boolean;
  onPay: (invoiceNo: string) => void;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} data-testid="finance-invoices" className={cardClass}>
      <h2 id={headingId} className={headingClass}>
        {t('clients.finance.invoices.title')}
      </h2>
      {invoices.length === 0 ? (
        <p className="m-0 text-[13px] text-cf-ink-subtle">{t('clients.finance.invoices.empty')}</p>
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0">
          <div role="table" aria-labelledby={headingId} className="flex flex-col">
            <div
              role="row"
              className={`${GRID} border-b border-cf-line pb-2 text-[12px] text-cf-ink-subtle`}
            >
              {(['no', 'car', 'due', 'outstanding', 'status', 'action'] as const).map((column) => (
                <span key={column} role="columnheader">
                  {t(`clients.finance.invoices.columns.${column}`)}
                </span>
              ))}
            </div>
            {invoices.map((invoice) => (
              <div
                key={invoice.no}
                role="row"
                data-testid={`finance-invoice-${invoice.no}`}
                className={`${GRID} border-b border-cf-line-faint py-2.5 text-[13px] text-cf-ink last:border-b-0`}
              >
                <span role="cell" className="font-mono">
                  {invoice.no}
                </span>
                <span role="cell" className="truncate">
                  {invoice.lotTitle ?? t(`adminFinance.invoiceKind.${invoice.kind}`)}
                </span>
                <span role="cell" className="font-mono">
                  {formatDate(invoice.dueAt)}
                </span>
                <span role="cell" className="font-mono">
                  {invoice.outstandingChf > 0 ? formatChf(invoice.outstandingChf) : '—'}
                </span>
                <span role="cell" className={INVOICE_STATUS_TEXT[invoice.displayStatus]}>
                  {t(invoiceStatusKey(invoice.displayStatus))}
                </span>
                <span role="cell">
                  {paymentsEnabled && isPayable(invoice) && (
                    <button
                      type="button"
                      onClick={() => onPay(invoice.no)}
                      aria-label={t('clients.finance.invoices.payAria', { no: invoice.no })}
                      className="h-8 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[12px] font-semibold text-cf-ink hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink"
                    >
                      {t('clients.finance.invoices.pay')}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
